import { Box, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { getMaskedValue } from "components/elements/MaskedModeWrapper";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import dayjs from "dayjs";
import { useAPIDashboardStrategiesCharts } from "hooks/useAPI";
import useExchangesMetadataGlobal from "hooks/useExchangesMetadataGlobal";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { createGlobalStyle } from "styled-components";
import { IInvestmentChart } from "types/types";
import { getColorByMode, splitCurrencyPair } from "utils";
import { renderPrice } from "utils/formatter";

import { SMALL_CHART_MAX_SAMPLES, SUMMARY_CHART_HEIGHT } from "..";

const GlobalStyle = createGlobalStyle`
  .summary-investment-chart .apexcharts-legend-series[rel="1"] {
    display: none !important;
  }
`;

interface Props {
  chartData: IInvestmentChart;
  currencyPair: string;
  isFullScreen?: boolean;
}

const InvestmentChart = ({ chartData, currencyPair, isFullScreen }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMasked, summariesSettings } = useUserDataContext();
  const { colors } = useMUIThemeModeContext();
  const { counterCurrDisplayedScale, counterCurrShortDisplayedScale, baseCurrDisplayedScale } = useExchangesMetadataGlobal(currencyPair);
  const { baseCurrency, counterCurrency } = splitCurrencyPair(currencyPair);

  const shouldFetchFullscreen = !!isFullScreen && chartData.points.length >= SMALL_CHART_MAX_SAMPLES;
  const { data: fullscreenData } = useAPIDashboardStrategiesCharts(shouldFetchFullscreen, { ...summariesSettings, fullscreen: true });

  const effectiveChartData: IInvestmentChart
    = shouldFetchFullscreen && fullscreenData?.chartsByCurrencyPair[currencyPair]?.investmentChart
      ? fullscreenData.chartsByCurrencyPair[currencyPair].investmentChart
      : chartData;

  const labels = effectiveChartData.points.map((item) =>
    Intl.DateTimeFormat("cs-CZ").format(new Date(dayjs(item.d).format()))
  );

  const series = [
    {
      name: t("amountAccumulated"),
      data: effectiveChartData.points.map((item) => item.a),
    },
    {
      name: t("invested"),
      data: effectiveChartData.points.map((item) => item.i),
    },
    {
      name: t("value"),
      data: effectiveChartData.points.map((item) => item.v),
    },
  ];

  const options: ApexOptions = {
    theme: { mode: theme.palette.mode },
    tooltip: {
      followCursor: true,
      inverseOrder: true,
      y: {
        formatter: (val: number, { seriesIndex }: any) => {
          // not possible to have a separate formatter for each data series, therefore this hack
          if (seriesIndex === 0) {
            return getMaskedValue(isMasked, renderPrice(val, baseCurrency, baseCurrDisplayedScale, true));
          } else {
            return getMaskedValue(isMasked, renderPrice(val, counterCurrency, counterCurrDisplayedScale));
          }
        },
      },
    },
    colors: [
      "transparent",
      getColorByMode(theme, theme.palette.grey[300], colors.black),
      theme.palette.primary.main,
    ],
    chart: {
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: false },
      background: "transparent",
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: ["stepline", "stepline", "smooth"] as ApexStroke["curve"],
      width: [0, 3, 3],
    },
    grid: {
      row: {
        colors: [colors.legacyGray100, "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        hideOverlappingLabels: true,
        rotate: -30,
        show: true,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      show: true,
      min: effectiveChartData.minValue,
      labels: {
        formatter: (val: any) => getMaskedValue(isMasked, renderPrice(val, counterCurrency, counterCurrShortDisplayedScale)),
      },
    },
  };

  return (
    <Box sx={{ pr: { xs: 0, md: 2 }, textAlign: "center", height: "100%" }}>
      <GlobalStyle />
      <ReactApexChart
        className="summary-investment-chart"
        options={options}
        series={series}
        type="line"
        width={"100%"}
        height={isFullScreen ? "95%" : SUMMARY_CHART_HEIGHT}
      />
    </Box>
  );
};

export default InvestmentChart;
